import React, { useState, useEffect } from "react";
import * as S from "./AuditoriaContratosStyle";
import { clientAdminService } from "../../Services/clientAdminService";
import { contractAdminService } from "../../Services/contractAdminService";
import { FaSearch, FaUserCircle, FaFilePdf, FaFileCsv } from "react-icons/fa";
import { formatCPF, formatCurrencyBRL, formatDate } from "../ASSETS/assets";
import Loading from "../Loader";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ModalContrato from "./ModalContrato/ModalContrato";

export default function AuditoriaContratos2() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [modalData, setModalData] = useState(null);

  const [filterId, setFilterId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [exportingNegative, setExportingNegative] = useState(false);

  // Lógica de busca de clientes com Debounce
  useEffect(() => {
    if (searchTerm.length > 2) {
      setSearchLoading(true);
      const delay = setTimeout(async () => {
        try {
          const data = await clientAdminService.searchClients(
            10,
            0,
            searchTerm
          );
          setSearchResults(data);
        } catch (e) {
          console.error(e);
        } finally {
          setSearchLoading(false);
        }
      }, 500);
      return () => clearTimeout(delay);
    } else {
      setSearchResults([]);
      setSearchLoading(false);
    }
  }, [searchTerm]);

  const handleSelectClient = async (client) => {
    setLoading(true);
    setSearchTerm("");
    setSearchResults([]);
    try {
      const data = await contractAdminService.getClientAuditDetails(client.id);
      setSelectedClientData(data);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDeviaTeValorizado = (item) => {
    const VALORIZACAO_DIARIA = 0.00136666;
    const dataInicio = item.contract.firstValorization;
    const dataFim =
      new Date() > new Date(item.contract.endContractDate)
        ? new Date(item.contract.endContractDate)
        : new Date();

    if (!dataInicio) return 0;

    const diasDecorridos = Math.floor(
      (dataFim - new Date(dataInicio)) / (1000 * 60 * 60 * 24)
    );
    const valorContrato = item.contract.totalPrice || 0;

    return diasDecorridos > 0
      ? diasDecorridos * VALORIZACAO_DIARIA * valorContrato
      : 0;
  };

  // Filtragem dos contratos na tabela (por ID ou Status)
  const filteredContracts =
    selectedClientData?.contracts?.filter((c) => {
      const matchId = filterId
        ? c.contract.id.toString().includes(filterId)
        : true;
      const matchStatus = filterStatus
        ? c.contract.status.toString() === filterStatus
        : true;
      return matchId && matchStatus;
    }) || [];

  // Consideramos apenas contratos ATIVOS (1) ou FINALIZADOS (2) para os totais do topo
  const contratosParaSoma = filteredContracts.filter(
    (c) => c.contract.status === 1 || c.contract.status === 2
  );

  // --- CÁLCULOS DOS CARDS (TOP SUMMARY) ---

  // 1. Soma do valor de compra (Total Valor de Compra)
  const totalValorCompra = contratosParaSoma.reduce(
    (acc, curr) => acc + (curr.contract.totalPrice || 0),
    0
  );

  // 2. Soma da projeção de 147%
  const totalProjecao150 = contratosParaSoma.reduce(
    (acc, curr) => acc + (curr.shouldProfit || 0),
    0
  );

  // 3. Soma do que já foi creditado/pago
  const totalRoyaltiesGerados = contratosParaSoma.reduce(
    (acc, curr) => acc + (curr.totalWithdrawn || 0),
    0
  );

  // 4. Soma do que resta a creditar (considera apenas valores positivos de profitLeft)
  const totalRestaCreditar = contratosParaSoma.reduce(
    (acc, curr) => acc + (curr.profitLeft > 0 ? curr.profitLeft : 0),
    0
  );

  // 5. Soma de excedentes (PLUS incorreto) - quando o lucro restante é negativo
  const totalPagoAMaisPlusIncorreto = contratosParaSoma.reduce((acc, curr) => {
    return acc + (curr.profitLeft < 0 ? Math.abs(curr.profitLeft) : 0);
  }, 0);

  // 6. Total que Devia ser creditado no sistema até hoje
  const totalDeviaSerCreditado = contratosParaSoma.reduce(
    (acc, curr) => acc + calculateDeviaTeValorizado(curr),
    0
  );

  // 7. Total que Foi creditado no sistema
  const totalFoiCreditadoSistema = contratosParaSoma.reduce(
    (acc, curr) => acc + ((curr.contract.actualValorization / 100) * curr.contract.totalPrice || 0),
    0
  );

  // 8. Total de que foi pago pro cliente
  const totalPagoCliente = contratosParaSoma.reduce(
    (acc, curr) => acc + (curr.totalWithdrawn || 0),
    0
  );

  // 9. Total de excedentes pagos (valores negativos de profitLeft transformados em positivo)
  const totalExcedentePago = contratosParaSoma.reduce((acc, curr) => {
    return acc + (curr.profitLeft < 0 ? Math.abs(curr.profitLeft) : 0);
  }, 0);

  // 10. Total pago acima do previsto — só o módulo do deficit negativo (creditado
  // no sistema além do que a taxa base 0,13666%/dia previa até hoje). Antes somava
  // saques pendentes junto; foi removido a pedido do jurídico.
  const totalPagoAcimaPrevisto = contratosParaSoma.reduce((acc, curr) => {
    const creditado = (curr.contract.actualValorization / 100) * curr.contract.totalPrice;
    const devia = calculateDeviaTeValorizado(curr);
    const deficit = devia - creditado;
    return acc + (deficit < 0 ? Math.abs(deficit) : 0);
  }, 0);

  // 11. Total de saques pendentes (valor na fila aguardando pagamento)
  const totalSaquesPendentes = contratosParaSoma.reduce(
    (acc, curr) => acc + (curr.totalPendingWithdrawal || 0),
    0
  );

  // 12. Total antecipado (soma de todas as antecipações pagas)
  const totalAntecipado = contratosParaSoma.reduce(
    (acc, curr) => acc + (curr.totalAntecipated || 0),
    0
  );

  // 13. Total Plus (R$) — teto do contrato acima de 147% aplicado sobre o preço
  const totalPlusValor = contratosParaSoma.reduce((acc, curr) => {
    const maxVal = curr.contract.maxValorization || 0;
    const plusPerc = maxVal - 147;
    return acc + (plusPerc >= 1 ? (plusPerc / 100) * curr.contract.totalPrice : 0);
  }, 0);

  // Lógica de Paginação
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredContracts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);

  const getStatusName = (s) => {
    if (s === 1) return "Valorizando";
    if (s === 2) return "Finalizado";
    if (s === 4) return "Pendente";
    return "N/A";
  };

  const normalizeName = (name) => {
    if (!name) return "cliente";
    return name
      .split(" ")[0]
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };

  const exportNegativeProfitCSV = async () => {
    setExportingNegative(true);
    try {
      const data = await contractAdminService.getNegativeProfitLeft();

      if (!data || data.length === 0) {
        alert("Nenhum contrato encontrado com Valor à Ser Pago negativo.");
        return;
      }

      const headers = [
        "Nome",
        "CPF/CNPJ",
        "ID do Contrato",
        "Data da Compra",
        "Status",
        "Valor do Contrato",
        "Max Valorization (%)",
        "Total Pago",
        "Projeção 147%",
        "Valor à Ser Pago",
        "Excedente",
      ];

      const escape = (val) => {
        const s = val === null || val === undefined ? "" : String(val);
        return `"${s.replace(/"/g, '""')}"`;
      };

      const rows = data.map((d) => [
        d.clientName || "",
        d.cpfCnpj || "",
        d.contractId,
        d.dateCreated ? formatDate(d.dateCreated) : "",
        getStatusName(d.status),
        (d.totalPrice ?? 0).toFixed(2).replace(".", ","),
        d.maxValorization ?? "",
        (d.totalWithdrawn ?? 0).toFixed(2).replace(".", ","),
        (d.shouldProfit ?? 0).toFixed(2).replace(".", ","),
        (d.profitLeft ?? 0).toFixed(2).replace(".", ","),
        Math.abs(d.profitLeft ?? 0).toFixed(2).replace(".", ","),
      ]);

      const csv = [headers, ...rows]
        .map((row) => row.map(escape).join(";"))
        .join("\r\n");

      const blob = new Blob(["﻿" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute("download", `contratos_valor_negativo_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Erro ao gerar relatório.");
    } finally {
      setExportingNegative(false);
    }
  };

  // Função para exportação de PDF
  const exportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    const clientName = selectedClientData.client.name;
    const clientCPF = formatCPF(selectedClientData.client.cpfCnpj);

    // Cabeçalho do PDF
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(220, 220, 220);
    doc.roundedRect(40, 30, 762, 75, 5, 5, "FD");
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`Cliente: ${clientName}`, 120, 58);
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(10);
    doc.text(`CPF: ${clientCPF}`, 120, 75);
    doc.setTextColor(177, 125, 42);
    doc.setFontSize(16);
    doc.text("Visão de Contratos e Benefícios", 530, 60);

    // Cards de resumo no PDF
    const summary = [
      {
        label: "Total Valor de Contratos",
        value: formatCurrencyBRL(totalValorCompra),
        color: [177, 125, 42],
      },
      {
        label: "Total Projetado de Valorização Contratual (até 147%)",
        value: formatCurrencyBRL(totalProjecao150),
        color: [41, 128, 185],
      },
      {
        label: "Devia ser creditado no sistema até hoje",
        value: formatCurrencyBRL(totalDeviaSerCreditado),
        color: [155, 89, 182],
      },
      {
        label: "Total que Foi creditado no sistema",
        value: formatCurrencyBRL(totalFoiCreditadoSistema),
        color: [243, 156, 18],
      },
      {
        label: "Total de que foi pago pro cliente",
        value: formatCurrencyBRL(totalPagoCliente),
        color: [39, 174, 96],
      },
      {
        label: "Total de excedentes pagos",
        value: formatCurrencyBRL(totalExcedentePago),
        color: [192, 57, 43],
      },
    ];

    summary.forEach((item, i) => {
      const cardsPerRow = 3;
      const row = Math.floor(i / cardsPerRow);
      const col = i % cardsPerRow;
      const cardWidth = 240;
      const cardHeight = 50;
      const padding = 5;
      const gapX = 8;
      const gapY = 65;

      const x = 40 + col * (cardWidth + gapX);
      const y = 120 + row * gapY;

      // Desenha o Card
      doc.setFillColor(item.color[0], item.color[1], item.color[2]);
      doc.roundedRect(x, y, cardWidth, cardHeight, 4, 4, "F");

      doc.setTextColor(255, 255, 255);

      // --- AJUSTE DO TÍTULO (LABEL) ---
      doc.setFontSize(7);
      const maxWidth = cardWidth - padding * 2;
      const labelLines = doc.splitTextToSize(item.label, maxWidth);

      // Renderiza as linhas
      doc.text(labelLines, x + padding, y + 12);

      // --- AJUSTE DO VALOR ---
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(item.value, x + padding, y + 38);
      doc.setFont("helvetica", "normal");
    });

    // Mapeamento da tabela para o PDF
    const tableRows = filteredContracts.map((item) => {
      const maxVal = item.contract.maxValorization || 0;
      const plusPerc = maxVal - 147;
      const valPlus =
        plusPerc >= 1 ? (plusPerc / 100) * item.contract.totalPrice : 0;
      const deviaValorizado = calculateDeviaTeValorizado(item);
      const deviaPercent = ((deviaValorizado / item.contract.totalPrice) * 100).toFixed(2);
      const creditadoSistema = (item.contract.actualValorization / 100) * item.contract.totalPrice;
      const actualValorizationFormatted = parseFloat(item.contract.actualValorization).toFixed(2);

      return [
        `#${item.contract.id}`,
        formatDate(item.contract.dateCreated),
        getStatusName(item.contract.status),
        item.contract.goldenBox ? "Sim" : "Não",
        formatCurrencyBRL(item.contract.totalPrice),
        formatCurrencyBRL(item.shouldProfit),
        `${formatCurrencyBRL(deviaValorizado)} (${deviaPercent}%)`,
        `${formatCurrencyBRL(creditadoSistema)} (${actualValorizationFormatted}%)`,
        formatCurrencyBRL(deviaValorizado - creditadoSistema),
        formatCurrencyBRL(item.totalWithdrawn),
        formatCurrencyBRL(item.totalPendingWithdrawal),
        formatCurrencyBRL(item.profitLeft),
        formatCurrencyBRL(item.totalAntecipated),
        plusPerc >= 1 ? `${plusPerc}%` : "0%",
        formatCurrencyBRL(valPlus),
      ];
    });

    autoTable(doc, {
      startY: 255,
      head: [
        [
          "ID",
          "Data da\nCompra",
          "Status",
          "Possui\nGolden Box?",
          "Valor do\nContrato",
          "Projeção\nTotal (147%)",
          "Devia ser\ncreditado no\nsistema até hoje",
          "Foi creditado\nno sistema",
          "Defcit (Devia\n- Cred)",
          "Foi Pago ao\ncliente",
          "Valor em\nSaques Pendentes",
          "Valor à\nser pago",
          "Valor\nAntecipado",
          "Plus\n(%)",
          "Plus\n(R$)",
        ],
      ],
      body: tableRows,
      theme: "grid",
      styles: { fontSize: 5 },
      headStyles: { fillColor: [177, 125, 42] },
    });

    doc.save(`auditoria_${normalizeName(clientName)}.pdf`);
  };

  console.log(filteredContracts);

  return (
    <S.PageContainer>
      <Loading load={loading} />
      {modalData && (
        <ModalContrato data={modalData} onClose={() => setModalData(null)} />
      )}

      <S.ContentBox>
        <S.Header>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <h2>Auditoria de Contratos</h2>
            <div style={{ display: "flex", gap: "10px" }}>
              <S.ActionButton
                onClick={exportNegativeProfitCSV}
                disabled={exportingNegative}
                style={{ background: "#c0392b" }}
              >
                <FaFileCsv />{" "}
                {exportingNegative ? "Gerando..." : "CSV Valor Negativo"}
              </S.ActionButton>
              {selectedClientData && (
                <S.ActionButton
                  onClick={exportPDF}
                  style={{ background: "#27ae60" }}
                >
                  <FaFilePdf /> Gerar PDF
                </S.ActionButton>
              )}
            </div>
          </div>
        </S.Header>

        {/* Seção de Busca */}
        <S.SearchSection>
          <S.SearchBar>
            <input
              type="text"
              placeholder="Pesquisar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button disabled={searchLoading}>
              {searchLoading ? "..." : <FaSearch />}
            </button>
          </S.SearchBar>
          {searchResults.length > 0 && (
            <S.ResultsDropdown>
              {searchResults.map((c) => (
                <S.ResultItem key={c.id} onClick={() => handleSelectClient(c)}>
                  <FaUserCircle /> {c.name} - {formatCPF(c.cpfCnpj)}
                </S.ResultItem>
              ))}
            </S.ResultsDropdown>
          )}
        </S.SearchSection>

        {selectedClientData && (
          <>
            <S.ClientHeader>
              <div>
                <p>
                  <strong>Cliente:</strong> {selectedClientData.client.name}
                </p>
              </div>
              <div>
                <p>
                  <strong>CPF:</strong>{" "}
                  {formatCPF(selectedClientData.client.cpfCnpj)}
                </p>
              </div>
              <div>
                <p>
                  <strong>E-mail:</strong> {selectedClientData.client.email}
                </p>
              </div>
            </S.ClientHeader>

            {/* CARDS DO TOPO ATUALIZADOS CONFORME SOLICITAÇÃO */}
            <S.SummaryGrid style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
              <S.SummaryCard borderColor="#b17d2a">
                <small>Total Valor de Contratos</small>
                <h3>{formatCurrencyBRL(totalValorCompra)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#2980b9">
                <small>
                  Total Projetado de Valorização Contratual (até 147%)
                </small>
                <h3>{formatCurrencyBRL(totalProjecao150)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#9b59b6">
                <small>Devia ser creditado no sistema até hoje</small>
                <h3>{formatCurrencyBRL(totalDeviaSerCreditado)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#f39c12">
                <small>Total que Foi creditado no sistema</small>
                <h3>{formatCurrencyBRL(totalFoiCreditadoSistema)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#27ae60">
                <small>Total de que foi pago pro cliente</small>
                <h3>{formatCurrencyBRL(totalPagoCliente)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#3498db">
                <small>Total de Saques Pendentes</small>
                <h3>{formatCurrencyBRL(totalSaquesPendentes)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#16a085">
                <small>Total Antecipado</small>
                <h3>{formatCurrencyBRL(totalAntecipado)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#8e44ad">
                <small>Total Plus (R$)</small>
                <h3>{formatCurrencyBRL(totalPlusValor)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#c0392b">
                <small>Total de excedentes pagos</small>
                <h3>{formatCurrencyBRL(totalExcedentePago)}</h3>
              </S.SummaryCard>
              <S.SummaryCard borderColor="#e74c3c">
                <small>Pagamento realizado acima do previsto</small>
                <h3>{formatCurrencyBRL(totalPagoAcimaPrevisto)}</h3>
              </S.SummaryCard>
            </S.SummaryGrid>

            {/* Filtros da Tabela */}
            <S.FilterGrid>
              <div className="group">
                <label>Filtrar ID:</label>
                <input
                  type="text"
                  value={filterId}
                  onChange={(e) => setFilterId(e.target.value)}
                />
              </div>
              <div className="group">
                <label>Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">Todos os Status</option>
                  <option value="1">Valorizando</option>
                  <option value="2">Finalizado</option>
                  <option value="4">Pendente</option>
                </select>
              </div>
              <div className="group">
                <label>Itens por página:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </S.FilterGrid>

            {/* Tabela de Contratos */}
            <S.TableContainer>
              <S.Table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Data da Compra</th>
                    <th>Status</th>
                    <th>Possui Golden Box?</th>
                    <th>Valor do Contrato</th>
                    <th>Projeção Total (147%)</th>
                    <th>Devia ser creditado no sistema até hoje</th>
                    <th>Foi creditado no sistema</th>
                    <th>Defcit (Oq foi creditado - Oq deveria)</th>
                    <th>Foi Pago ao cliente</th>
                    <th>Valor em Saques Pendentes</th>
                    <th>Valor à ser pago</th>
                    <th>Valor Antecipado</th>
                    <th>Plus (%)</th>
                    <th>Plus (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item) => {
                      const maxVal = item.contract.maxValorization || 0;
                      const plusResult = maxVal - 147;
                      const valPlus =
                        plusResult >= 1
                          ? (plusResult / 100) * item.contract.totalPrice
                          : 0;
                      return (
                        <tr
                          key={item.contract.id}
                          onClick={() => setModalData(item)}
                        >
                          <td>#{item.contract.id}</td>
                          <td>{formatDate(item.contract.dateCreated)}</td>
                          <td>{getStatusName(item.contract.status)}</td>
                          <td>
                            {item.contract.goldenBox
                              ? `Sim (#${item.contract.goldenBox.id})`
                              : "Não"}
                          </td>
                          <td>{formatCurrencyBRL(item.contract.totalPrice)}</td>
                          <td className="profit">
                            {formatCurrencyBRL(item.shouldProfit)}
                          </td>
                          <td style={{ color: "#ffffff", fontWeight: 600 }}>
                            {formatCurrencyBRL(
                              calculateDeviaTeValorizado(item)
                            )}{" "}
                            (
                            {formatCurrencyBRL(
                              (calculateDeviaTeValorizado(item) /
                                item.contract.totalPrice) *
                                100
                            )}
                            %)
                          </td>
                          <td style={{ color: "#ffffff", fontWeight: 600 }}>
                            {formatCurrencyBRL(
                              (item.contract.actualValorization / 100) *
                                item.contract.totalPrice
                            )}
                            {" "}
                            (
                            {formatCurrencyBRL(
                              item.contract.actualValorization
                            )}%)
                          </td>
                          {(() => {
                            const creditado =
                              (item.contract.actualValorization / 100) *
                              item.contract.totalPrice;
                            const devia = calculateDeviaTeValorizado(item);
                            const deficit = devia - creditado;
                            return (
                              <td
                                style={{
                                  color: deficit < 0 ? "#e74c3c" : "#27ae60",
                                  fontWeight: 600,
                                }}
                              >
                                {formatCurrencyBRL(deficit)}
                              </td>
                            );
                          })()}
                          <td>{formatCurrencyBRL(item.totalWithdrawn)}</td>
                          <td style={{ color: "#3498db" }}>
                            {formatCurrencyBRL(item.totalPendingWithdrawal)}
                          </td>
                          <td
                            className={item.profitLeft > 0 ? "profit" : ""}
                            style={
                              item.profitLeft < 0
                                ? { color: "#e74c3c", fontWeight: "bold" }
                                : {}
                            }
                          >
                            {formatCurrencyBRL(item.profitLeft)}
                          </td>

                          <td style={{ color: "#3498db" }}>
                            {formatCurrencyBRL(item.totalAntecipated)}
                          </td>
                          <td>
                            {plusResult >= 1 ? `${plusResult}%` : "Sem Plus"}
                          </td>
                          <td>
                            {plusResult >= 1
                              ? formatCurrencyBRL(valPlus)
                              : "Sem Plus"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="12" style={{ textAlign: "center" }}>
                        Nenhum contrato encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </S.Table>
            </S.TableContainer>

            {/* Navegação de Páginas */}
            <S.PaginationContainer>
              <S.PaginationButton
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Anterior
              </S.PaginationButton>
              <span>
                Página {currentPage} de {totalPages || 1}
              </span>
              <S.PaginationButton
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Próxima
              </S.PaginationButton>
            </S.PaginationContainer>
          </>
        )}
      </S.ContentBox>
    </S.PageContainer>
  );
}
